import { cs } from '../../lib/currency';
import { useEffect, useState, useMemo } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import api from '../../services/api';
import { toast } from 'sonner';
import { useFeatures } from '../../context/FeatureContext';
import {
  Clock, Play, Pencil, History, Lock, CheckCircle2, XCircle, RefreshCw,
  AlertTriangle,
} from 'lucide-react';

// iter335d — hide background jobs whose governing plan feature the tenant
// isn't entitled to. A job shows when it has NO gate, or when ANY of its
// featureKeys is entitled. Infra / payments / backup jobs are ungated.
const JOB_FEATURE_GATE = {
  enrollment_auto_resume_daily: ['enrollments.core'],
  enrollment_auto_resume_periodic: ['enrollments.core'],
  enrollment_scheduled_cancel_daily: ['enrollments.core'],
  enrollment_scheduled_cancel_periodic: ['enrollments.core'],
  cancellation_reminder_daily: ['enrollments.core'],
  invoice_reminder_daily: ['enrollments.core'],
  monthly_billing: ['enrollments.core'],
  monthly_billing_watchdog: ['enrollments.core'],
  payment_reminders_daily: ['enrollments.core'],
  auto_pay_retry_daily: ['enrollments.core'],
  birthday_emails_daily: ['enrollments.core'],
  auto_invoice_reminders_daily: ['enrollments.core', 'tournaments.registrations', 'bookings.lanes', 'bookings.small_groups'],
  game_archive_daily: ['tournaments.games'],
  game_archive_periodic: ['tournaments.games'],
  tournament_payment_cleanup: ['tournaments.registrations'],
  tournament_payment_amount_backfill_daily: ['tournaments.registrations'],
  lane_payment_cleanup: ['bookings.lanes'],
  whatsapp_rsvp_reminders_hourly: ['comms.whatsapp'],
  whatsapp_billing_reminders_daily: ['comms.whatsapp'],
  scheduled_email_campaigns_dispatch: ['comms.campaigns'],
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const triggerLabel = (t) => t?.human || (t?.type === 'cron' ? 'cron' : t?.type === 'interval' ? 'interval' : '—');

const CronTableRows = ({ jobs, busyId, onToggle, onEdit, onRun, onHistory }) => (
  <>
    {jobs.map((job) => {
      const locked = job.cadence_locked;
      const last = job.last_run;
      return (
        <tr key={job.id} className="border-b last:border-b-0 hover:bg-slate-50/60" data-testid={`cron-job-row-${job.id}`}>
          <td className="px-4 py-3 align-top">
            <div className="font-medium text-slate-900 flex items-center gap-2">
              {job.name}
              {locked && (
                <span title="Frequency locked" className="text-amber-600">
                  <Lock className="w-3.5 h-3.5" />
                </span>
              )}
              {job.sensitive && (
                <Badge className="bg-rose-50 text-rose-700 text-[10px]">sensitive</Badge>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{job.description}</div>
            <div className="text-[10px] text-slate-400 mt-1 font-mono">{job.id}</div>
          </td>
          <td className="px-4 py-3 align-top whitespace-nowrap">
            <div className="text-slate-700">{triggerLabel(job.effective_trigger)}</div>
            {job.override?.trigger && (
              <div className="text-[10px] text-slate-400 italic">overridden</div>
            )}
          </td>
          <td className="px-4 py-3 align-top text-slate-700 whitespace-nowrap">{fmtDate(job.next_run_at)}</td>
          <td className="px-4 py-3 align-top text-xs">
            {last ? (
              <div className="flex items-start gap-1.5">
                {last.ok ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5" />
                ) : (
                  <XCircle className="w-3.5 h-3.5 text-rose-600 mt-0.5" />
                )}
                <div>
                  <div className="text-slate-700">{fmtDate(last.started_at)}</div>
                  <div className="text-slate-500">{last.duration_ms} ms</div>
                  {!last.ok && last.error && (
                    <div className="text-rose-600 max-w-[200px] truncate" title={last.error}>{last.error}</div>
                  )}
                </div>
              </div>
            ) : (
              <span className="text-slate-400">never</span>
            )}
          </td>
          <td className="px-4 py-3 align-top">
            <Switch
              checked={!!job.enabled}
              disabled={busyId === job.id}
              onCheckedChange={(v) => onToggle(job, v)}
              data-testid={`cron-job-toggle-${job.id}`}
            />
          </td>
          <td className="px-4 py-3 align-top text-right whitespace-nowrap">
            <Button
              variant="ghost" size="sm"
              className="text-blue-700"
              disabled={locked || busyId === job.id}
              onClick={() => onEdit(job)}
              data-testid={`cron-job-edit-${job.id}`}
            >
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
            <Button
              variant="ghost" size="sm"
              className="text-emerald-700"
              disabled={!job.enabled || busyId === job.id}
              onClick={() => onRun(job)}
              data-testid={`cron-job-run-${job.id}`}
            >
              <Play className="w-3.5 h-3.5 mr-1" /> Run now
            </Button>
            <Button
              variant="ghost" size="sm"
              onClick={() => onHistory(job)}
              data-testid={`cron-job-history-${job.id}`}
            >
              <History className="w-3.5 h-3.5 mr-1" /> History
            </Button>
          </td>
        </tr>
      );
    })}
  </>
);

const CronJobsSettings = () => {
  const { isPlanFeatureEnabled } = useFeatures();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [editing, setEditing] = useState(null);          // {job, draft}
  const [history, setHistory] = useState(null);          // {job, runs}
  const [confirmRun, setConfirmRun] = useState(null);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/cron-jobs');
      setJobs(data.jobs || []);
    } catch {
      toast.error('Failed to load cron jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchJobs(); }, []);

  // iter335d — plan-gate the visible job list before deriving categories.
  const visibleJobs = useMemo(() => jobs.filter((j) => {
    const gate = JOB_FEATURE_GATE[j.id];
    if (!gate) return true;
    return gate.some((k) => isPlanFeatureEnabled(k));
  }), [jobs, isPlanFeatureEnabled]);

  const categories = useMemo(() => {
    const s = new Set(visibleJobs.map((j) => j.category));
    return ['all', ...Array.from(s).sort()];
  }, [visibleJobs]);

  const filtered = useMemo(() => {
    if (activeCategory === 'all') return visibleJobs;
    return visibleJobs.filter((j) => j.category === activeCategory);
  }, [visibleJobs, activeCategory]);

  const toggleEnabled = async (job, next) => {
    setBusyId(job.id);
    try {
      await api.patch(`/admin/cron-jobs/${job.id}`, { enabled: next });
      toast.success(`${job.name} ${next ? 'enabled' : 'disabled'}`);
      await fetchJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update job');
    } finally {
      setBusyId(null);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const { job, draft } = editing;
    const trigger = draft.type === 'interval'
      ? { type: 'interval', ...(draft.unit === 'seconds' ? { seconds: Number(draft.value) }
            : draft.unit === 'minutes' ? { minutes: Number(draft.value) }
            : { hours: Number(draft.value) }) }
      : { type: 'cron', hour: Number(draft.hour), minute: Number(draft.minute), ...(draft.timezone ? { timezone: draft.timezone } : {}) };

    setBusyId(job.id);
    try {
      await api.patch(`/admin/cron-jobs/${job.id}`, { trigger });
      toast.success('Frequency updated');
      setEditing(null);
      await fetchJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update frequency');
    } finally {
      setBusyId(null);
    }
  };

  const runNow = async (job) => {
    setBusyId(job.id);
    try {
      await api.post(`/admin/cron-jobs/${job.id}/run-now`);
      toast.success(`${job.name} triggered`);
      // Give the recorder a beat to land the row.
      setTimeout(fetchJobs, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run job');
    } finally {
      setBusyId(null);
      setConfirmRun(null);
    }
  };

  const openHistory = async (job) => {
    try {
      const { data } = await api.get(`/admin/cron-jobs/${job.id}/runs`);
      // iter334c v8 — For the monthly-billing job, ALSO pull the
      // structured billing summary events so admins can see
      // per-run counters (invoices_created, auto_charged, errors,
      // skip_reason) + per-enrollment error tracebacks without
      // DevTools. Falls back gracefully if the endpoint isn't
      // available yet on older deployments.
      let billingHistory = null;
      if (job.id === 'monthly_billing') {
        try {
          const r = await api.get('/admin/billing/monthly-cron-runs?limit=20');
          billingHistory = r.data || null;
        } catch (e) {
          // Non-fatal — old-deploy fallback.
          console.warn('[CronJobs] billing history unavailable:', e?.message);
        }
      }
      setHistory({ job, runs: data.runs || [], billing: billingHistory });
    } catch {
      toast.error('Failed to load run history');
    }
  };

  const openEdit = (job) => {
    const t = job.effective_trigger || job.default_trigger || {};
    if (t.type === 'interval') {
      const sec = t.seconds || 60;
      let unit = 'seconds', value = sec;
      if (sec >= 3600 && sec % 3600 === 0) { unit = 'hours'; value = sec / 3600; }
      else if (sec >= 60 && sec % 60 === 0) { unit = 'minutes'; value = sec / 60; }
      setEditing({ job, draft: { type: 'interval', unit, value: String(value) } });
    } else {
      const f = t.all_fields || t.fields || {};
      setEditing({
        job,
        draft: {
          type: 'cron',
          hour: (f.hour && f.hour !== '*') ? f.hour : '0',
          minute: (f.minute && f.minute !== '*') ? f.minute : '0',
          timezone: t.timezone || '',
        },
      });
    }
  };

  return (
    <DashboardLayout title="Cron Jobs">
      <div className="space-y-6">
        <Card data-testid="cron-jobs-header-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-600" />
                  Background Jobs
                </CardTitle>
                <CardDescription>
                  Enable/disable scheduled tasks, change their frequency, view recent runs, or trigger a one-off run.
                  Critical infra jobs (Stripe fast-sync, Stripe checkout cleanups) are <span className="font-semibold">cadence-locked</span>.
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchJobs} data-testid="cron-jobs-refresh">
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeCategory} onValueChange={setActiveCategory}>
              <TabsList className="flex flex-wrap h-auto">
                {categories.map((c) => (
                  <TabsTrigger key={c} value={c} data-testid={`cron-jobs-tab-${c}`}>
                    {c === 'all' ? 'All' : c}
                    <span className="ml-1.5 text-[10px] opacity-60">
                      ({c === 'all' ? visibleJobs.length : visibleJobs.filter((j) => j.category === c).length})
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </CardContent>
        </Card>

        <Card data-testid="cron-jobs-table-card">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading…</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cron-jobs-table">
                  <thead className="bg-slate-50 border-b text-xs uppercase text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2">Job</th>
                      <th className="text-left px-4 py-2">Frequency</th>
                      <th className="text-left px-4 py-2">Next Run</th>
                      <th className="text-left px-4 py-2">Last Run</th>
                      <th className="text-left px-4 py-2">Enabled</th>
                      <th className="text-right px-4 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <CronTableRows
                      jobs={filtered}
                      busyId={busyId}
                      onToggle={toggleEnabled}
                      onEdit={openEdit}
                      onRun={setConfirmRun}
                      onHistory={openHistory}
                    />
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit-frequency dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-md" data-testid="cron-job-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Frequency</DialogTitle>
            <DialogDescription>{editing?.job?.name}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs">Trigger type</Label>
                <Select
                  value={editing.draft.type}
                  onValueChange={(v) => setEditing({ ...editing, draft: { ...editing.draft,
                    type: v,
                    ...(v === 'interval' ? { unit: 'hours', value: '1' } : { hour: '8', minute: '0' }),
                  } })}
                >
                  <SelectTrigger data-testid="cron-job-edit-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cron">Cron (specific time of day)</SelectItem>
                    <SelectItem value="interval">Interval (every X)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {editing.draft.type === 'interval' ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Every</Label>
                    <Input
                      type="number" min="1"
                      value={editing.draft.value}
                      onChange={(e) => setEditing({ ...editing, draft: { ...editing.draft, value: e.target.value } })}
                      data-testid="cron-job-edit-interval-value"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Unit</Label>
                    <Select
                      value={editing.draft.unit}
                      onValueChange={(v) => setEditing({ ...editing, draft: { ...editing.draft, unit: v } })}
                    >
                      <SelectTrigger data-testid="cron-job-edit-interval-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="seconds">seconds</SelectItem>
                        <SelectItem value="minutes">minutes</SelectItem>
                        <SelectItem value="hours">hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Hour (0–23)</Label>
                    <Input
                      type="number" min="0" max="23"
                      value={editing.draft.hour}
                      onChange={(e) => setEditing({ ...editing, draft: { ...editing.draft, hour: e.target.value } })}
                      data-testid="cron-job-edit-cron-hour"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Minute (0–59)</Label>
                    <Input
                      type="number" min="0" max="59"
                      value={editing.draft.minute}
                      onChange={(e) => setEditing({ ...editing, draft: { ...editing.draft, minute: e.target.value } })}
                      data-testid="cron-job-edit-cron-minute"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Timezone</Label>
                    <Input
                      placeholder="UTC"
                      value={editing.draft.timezone}
                      onChange={(e) => setEditing({ ...editing, draft: { ...editing.draft, timezone: e.target.value } })}
                      data-testid="cron-job-edit-cron-tz"
                    />
                  </div>
                </div>
              )}
              <div className="text-[11px] text-slate-500 flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-500" />
                Changes apply live — the job is re-registered immediately. No restart needed.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busyId === editing?.job?.id} data-testid="cron-job-edit-save">
              Save frequency
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run-now confirmation (per requirement: confirm for ALL jobs) */}
      <AlertDialog open={!!confirmRun} onOpenChange={(o) => { if (!o) setConfirmRun(null); }}>
        <AlertDialogContent data-testid="cron-job-run-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Run this job now?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block font-medium text-slate-800">{confirmRun?.name}</span>
              <span className="block text-xs mt-1">{confirmRun?.description}</span>
              {confirmRun?.sensitive && (
                <span className="block mt-2 text-rose-700 text-xs">
                  ⚠ This job has user-visible side effects (emails, billing, payment state). Confirm only if you mean to fire it outside its schedule.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmRun && runNow(confirmRun)}
              data-testid="cron-job-run-confirm-button"
            >
              Run now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Run history drawer */}
      <Dialog open={!!history} onOpenChange={(o) => { if (!o) setHistory(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cron-job-history-dialog">
          <DialogHeader>
            <DialogTitle>Run History</DialogTitle>
            <DialogDescription>{history?.job?.name} — last {history?.runs?.length || 0} runs</DialogDescription>
          </DialogHeader>

          {/* iter334c v8 — Billing-specific summary: counters,
              skip reasons, top-level exceptions, per-enrollment
              error rows. Only shown for the monthly-billing job. */}
          {history?.job?.id === 'monthly_billing' && history?.billing?.runs?.length > 0 && (
            <div className="mb-4 space-y-3" data-testid="cron-billing-summary-section">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                Monthly billing summary (last {history.billing.runs.length} run{history.billing.runs.length === 1 ? '' : 's'})
              </div>
              {history.billing.runs.map((r, idx) => {
                const isSkip = !!r.skipped;
                const isCrash = !!r.top_level_exception;
                const tone = isCrash
                  ? 'border-rose-300 bg-rose-50'
                  : isSkip
                    ? 'border-amber-300 bg-amber-50'
                    : (r.errors ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50');
                return (
                  <div key={idx} className={`rounded border ${tone} px-3 py-2 text-xs`} data-testid={`billing-summary-run-${idx}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-slate-900">{fmtDate(r.ts)}</div>
                      <Badge variant="outline" className="text-[10px]">
                        {r.billing_month}
                      </Badge>
                    </div>
                    {isCrash ? (
                      <div className="mt-1">
                        <div className="text-rose-800 font-semibold">
                          Crashed: {r.exception_class}: {r.exception_message}
                        </div>
                        {r.traceback_tail && (
                          <pre className="mt-1 max-h-40 overflow-auto bg-slate-900 text-slate-100 p-2 rounded text-[10px] whitespace-pre-wrap">{r.traceback_tail}</pre>
                        )}
                      </div>
                    ) : isSkip ? (
                      <div className="mt-1 text-amber-900">
                        <div>Skipped: <span className="font-mono">{r.skip_reason}</span></div>
                        {r.skip_reason === 'another_run_active' && r.lock_held_by && (
                          <div className="mt-0.5">Lock held by <span className="font-mono">{r.lock_held_by}</span>, expires {r.lock_expires_at || '—'}</div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2 text-slate-700">
                        <div><div className="text-[10px] text-slate-500">candidates</div><div className="font-semibold">{r.candidates_enumerated ?? '—'}</div></div>
                        <div><div className="text-[10px] text-slate-500">invoices</div><div className="font-semibold text-emerald-700">{r.invoices_created ?? 0}</div></div>
                        <div><div className="text-[10px] text-slate-500">auto-charged</div><div className="font-semibold">{r.auto_charged ?? 0}</div></div>
                        <div><div className="text-[10px] text-slate-500">errors</div><div className={`font-semibold ${(r.errors || 0) > 0 ? 'text-rose-700' : ''}`}>{r.errors ?? 0}</div></div>
                      </div>
                    )}
                  </div>
                );
              })}
              {history.billing.errors?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-rose-700 uppercase tracking-wide flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Per-enrollment errors ({history.billing.errors.length})
                  </div>
                  <div className="mt-1.5 space-y-1.5">
                    {history.billing.errors.slice(0, 20).map((e, idx) => (
                      <details key={idx} className="rounded border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs" data-testid={`billing-error-row-${idx}`}>
                        <summary className="cursor-pointer">
                          <span className="font-medium">{e.player_name || '(unknown)'}</span>
                          <span className="text-slate-500"> · {fmtDate(e.ts)} · </span>
                          <span className="font-mono text-rose-700">{e.exception_class}: {(e.exception_message || '').slice(0, 80)}</span>
                        </summary>
                        <div className="mt-1.5 space-y-1">
                          <div className="text-slate-600">enrollment_id: <span className="font-mono">{e.enrollment_id}</span></div>
                          {e.next_billing_date && <div className="text-slate-600">next_billing_date: {e.next_billing_date}</div>}
                          {e.monthly_fee != null && <div className="text-slate-600">monthly_fee: {cs()}{e.monthly_fee}</div>}
                          {e.traceback_tail && (
                            <pre className="max-h-32 overflow-auto bg-slate-900 text-slate-100 p-2 rounded text-[10px] whitespace-pre-wrap">{e.traceback_tail}</pre>
                          )}
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="space-y-1">
            {!history?.runs?.length && (
              <div className="text-sm text-slate-500 text-center py-6">No runs recorded yet.</div>
            )}
            {history?.runs?.map((r, idx) => (
              <div key={idx} className={`flex items-start gap-3 px-3 py-2 rounded border ${r.ok ? 'border-emerald-200 bg-emerald-50/30' : 'border-rose-200 bg-rose-50/30'}`}>
                {r.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> : <XCircle className="w-4 h-4 text-rose-600 mt-0.5" />}
                <div className="flex-1 text-xs">
                  <div className="font-medium text-slate-800">{fmtDate(r.started_at)}</div>
                  <div className="text-slate-500">Duration: {r.duration_ms} ms</div>
                  {r.error && <div className="text-rose-700 mt-0.5">{r.error}</div>}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CronJobsSettings;
